import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Instagram, Phone } from 'lucide-react';
import { navLinks, LOGO_URL, CONTACT } from '../mock';
import { Button } from './ui/button';

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const phoneRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!phoneOpen) return;
    const close = (e) => { if (phoneRef.current && !phoneRef.current.contains(e.target)) setPhoneOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [phoneOpen]);
  const { pathname, hash } = useLocation();

  const isActive = (path) => {
    if (path === '/') return pathname === '/' && !hash;
    if (path.startsWith('/#')) return pathname === '/' && hash === path.slice(1);
    return pathname === path;
  };

  const handleNavClick = (e, path) => {
    // Special handling for Startseite: scroll to top if already on home
    if (path === '/') {
      if (pathname === '/') {
        e.preventDefault();
        if (hash) {
          navigate('/', { replace: true });
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
    // For anchor links: if already on home, force smooth scroll even without route change
    if (path.startsWith('/#') && pathname === '/') {
      e.preventDefault();
      const el = document.getElementById(path.slice(2));
      if (el) {
        navigate(path, { replace: true });
        const y = el.getBoundingClientRect().top + window.pageYOffset - 80;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-3 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" onClick={(e) => handleNavClick(e, '/')} className="flex items-center gap-3 group">
          <div className="relative h-14 w-14 flex items-center justify-center">
            <img
              src={LOGO_URL}
              alt="AvantiTutor Logo"
              className="h-full w-full object-contain group-hover:scale-110 transition-transform"
              style={{ mixBlendMode: 'multiply' }}
            />
          </div>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-extrabold text-lg tracking-wider" style={{ color: '#3d94b8' }}>AVANTITUTOR</span>
            <span className="text-[9px] font-bold tracking-widest text-slate-500 uppercase">
              {CONTACT.tagline}
            </span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              onClick={(e) => handleNavClick(e, l.path)}
              className={`nav-link text-[15px] whitespace-nowrap ${isActive(l.path) ? 'active' : ''}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <div ref={phoneRef} className="hidden md:flex items-center">
            <div
              className="flex items-center h-10 rounded-full bg-slate-50 border border-slate-100 overflow-hidden transition-[max-width] duration-300 ease-out"
              style={{ maxWidth: phoneOpen ? 240 : 40 }}
            >
              <button
                type="button"
                data-testid="navbar-phone-toggle"
                onClick={() => setPhoneOpen((v) => !v)}
                aria-label={phoneOpen ? 'Telefonnummer ausblenden' : 'Telefonnummer anzeigen'}
                aria-expanded={phoneOpen}
                className="h-10 w-10 shrink-0 flex items-center justify-center hover:bg-slate-100 transition-colors"
              >
                <Phone className="h-4 w-4" style={{ color: '#3d94b8' }} />
              </button>
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                data-testid="navbar-phone-link"
                tabIndex={phoneOpen ? 0 : -1}
                className={`pr-4 pl-1 text-sm font-semibold whitespace-nowrap transition-opacity duration-200 hover:underline ${phoneOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                style={{ color: '#1a3a5c' }}
              >
                {CONTACT.phone}
              </a>
            </div>
          </div>
          <a
            href={CONTACT.instagram}
            target="_blank"
            rel="noreferrer"
            className="hidden md:inline-flex h-10 w-10 items-center justify-center rounded-full bg-white border border-slate-100 text-slate-600 hover:text-pink-500 hover:border-pink-200 transition-colors"
            aria-label="Instagram"
          >
            <Instagram className="h-5 w-5" />
          </a>
          <Button
            onClick={() => navigate('/portal')}
            variant="ghost"
            className="hidden md:inline-flex rounded-full font-semibold hover:bg-slate-100"
            style={{ color: '#2d7d9a' }}
          >
            Schüler-Portal
          </Button>
          <Button
            onClick={() => navigate('/buchen')}
            className="hidden md:inline-flex btn-teal rounded-full px-5"
          >
            Jetzt buchen
          </Button>
          <button className="lg:hidden h-10 w-10 rounded-full bg-white border border-slate-100 flex items-center justify-center" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <div className="lg:hidden bg-white border-t border-slate-100">
          <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-3">
            {navLinks.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                onClick={(e) => handleNavClick(e, l.path)}
                className={`py-2 font-semibold border-b border-slate-100 ${isActive(l.path) ? 'brand-teal' : 'brand-navy'}`}
              >
                {l.label}
              </Link>
            ))}
            <a href={`tel:${CONTACT.phoneRaw}`} className="py-2 font-semibold flex items-center gap-2" style={{ color: '#1a3a5c' }}>
              <Phone className="h-4 w-4" style={{ color: '#3d94b8' }} /> {CONTACT.phone}
            </a>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 rounded-full btn-outline-teal" onClick={() => { setOpen(false); navigate('/portal'); }}>Schüler-Portal</Button>
              <Button className="flex-1 rounded-full btn-teal" onClick={() => { setOpen(false); navigate('/buchen'); }}>Jetzt buchen</Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
